<template>
  <!-- Header - Only visible for non-admin pages -->
  <Header 
    v-if="!isAdminPage"
    :locale="locale" 
    @changeLanguage="changeLanguage" 
    @navigate="smoothScrollTo"
  />
  
  <!-- Main content -->
  <component 
    :is="currentComponent" 
    :locale="locale" 
    @changeLanguage="changeLanguage" 
    @navigate="handleNavigation" 
  />
  
  <!-- Scroll to Top Button - Only visible for non-admin pages -->
  <button 
    v-if="!isAdminPage"
    class="scroll-to-top-fixed" 
    @click="scrollToTop"
    :title="locale === 'tr' ? 'Yukarı Git' : 'Go to Top'"
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
    </svg>
  </button>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useGoogleAnalytics } from './composables/useGoogleAnalytics.js'
import Home from './views/Home.vue'
import ProjectDetail from './views/ProjectDetail.vue'
import AllProjects from './views/AllProjects.vue'
import MachineryDetail from './views/MachineryDetail.vue'
import AdminLogin from './views/AdminLogin.vue'
import AdminDashboard from './views/AdminDashboard.vue'
import Header from './components/Header.vue'

// Google Analytics Setup - G-4DBXPBFJT9 ID'si aktif!
const { trackEvent, trackPageView, trackUserInteraction, trackScrollDepth } = useGoogleAnalytics('G-4DBXPBFJT9')

// Components object for dynamic component
const components = {
  Home,
  ProjectDetail,
  AllProjects,
  MachineryDetail,
  AdminLogin,
  AdminDashboard
}

const currentComponent = ref(Home)

// Language and navigation state
const locale = ref('tr')

// Admin page detection
const isAdminPage = computed(() => {
  return currentComponent.value === AdminLogin || currentComponent.value === AdminDashboard
})

const changeLanguage = (lang) => {
  locale.value = lang
}

const smoothScrollTo = (selector) => {
  let element
  
  // Eğer selector # ile başlıyorsa ID selector olarak kullan
  if (selector.startsWith('#')) {
    element = document.querySelector(selector)
  } 
  // Eğer selector . ile başlıyorsa class selector olarak kullan
  else if (selector.startsWith('.')) {
    element = document.querySelector(selector)
  }
  // Eğer sadece isim verilmişse ID olarak dene
  else {
    element = document.getElementById(selector) || document.querySelector(selector)
  }
  
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  } else {
    console.warn(`Element not found: ${selector}`)
  }
}

const handleNavigation = (target) => {
  // Track navigation events
  if (target === 'home') {
    currentComponent.value = Home
    window.history.pushState({}, '', '/')
    trackPageView('Ana Sayfa', '/')
  } else if (target === 'admin-login') {
    currentComponent.value = AdminLogin  
    window.history.pushState({}, '', '/?page=admin-login')
    trackPageView('Admin Giriş', '/?page=admin-login')
  } else if (target === 'admin-dashboard') {
    currentComponent.value = AdminDashboard
    window.history.pushState({}, '', '/?page=admin-dashboard')
    trackPageView('Admin Panel', '/?page=admin-dashboard')
  } else {
    // Regular scroll navigation
    trackUserInteraction(`scroll_to_${target}`, 'navigation')
    smoothScrollTo(target)
  }
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

const checkRoute = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const page = urlParams.get('page')
  
  if (page === 'admin-login') {
    currentComponent.value = AdminLogin
  } else if (page === 'admin-dashboard') {
    // Check authentication
    const isAuthenticated = localStorage.getItem('admin_token')
    if (isAuthenticated) {
      currentComponent.value = AdminDashboard
    } else {
      currentComponent.value = AdminLogin
      window.history.pushState({}, '', '/?page=admin-login')
    }
  } else if (page === 'project' && urlParams.get('id')) {
    currentComponent.value = ProjectDetail
  } else if (page === 'projects' || page === 'all-projects') {
    currentComponent.value = AllProjects
  } else if (page === 'machinery' && urlParams.get('id')) {
    currentComponent.value = MachineryDetail
  } else {
    currentComponent.value = Home
  }
}

onMounted(() => {
  checkRoute()
  window.addEventListener('popstate', checkRoute)
  
  // Initialize scroll tracking for engagement
  const removeScrollTracker = trackScrollDepth()
  
  // Listen for admin logout events
  window.addEventListener('admin-logout', () => {
    currentComponent.value = AdminLogin
    window.history.pushState({}, '', '/?page=admin-login')
    trackPageView('Admin Giriş', '/?page=admin-login')
  })
  
  // Track initial page view
  trackPageView('Ana Sayfa', window.location.pathname + window.location.search)
})

onUnmounted(() => {
  window.removeEventListener('popstate', checkRoute)
  window.removeEventListener('admin-logout', () => {})
})
</script>

<style>
/* Fixed Scroll to Top Button - Bottom Right Corner - Golden Theme */
.scroll-to-top-fixed {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #F7C52D 0%, #E6B800 100%);
  color: #2C3E50;
  border: 2px solid #CC9900;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(247, 197, 45, 0.3);
  transition: all 0.3s ease;
  z-index: 9999;
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
  font-weight: bold;
}

.scroll-to-top-fixed:hover {
  background: linear-gradient(135deg, #F9D55A 0%, #F7C52D 100%);
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 8px 30px rgba(247, 197, 45, 0.4);
  border-color: #F7C52D;
}

.scroll-to-top-fixed:active {
  transform: translateY(-1px);
}

/* Always visible scroll button */

/* Responsive */
@media (max-width: 768px) {
  .scroll-to-top-fixed {
    bottom: 20px;
    right: 20px;
    width: 50px;
    height: 50px;
  }
}

@media (max-width: 480px) {
  .scroll-to-top-fixed {
    bottom: 15px;
    right: 15px;
    width: 45px;
    height: 45px;
  }
}

/* Hide main header on admin pages */
body.admin-page .header,
body.admin-page .yeo-header,
body.admin-page .header-top,
body.admin-page [data-v-61dd7a3d],
body.admin-page [data-v-61dd7a3d].header-top {
  display: none !important;
}

/* Remove top spacing on admin pages */
body.admin-page {
  margin: 0 !important;
  padding: 0 !important;
}

body.admin-page #app {
  margin: 0 !important;
  padding: 0 !important;
}
</style>